import { toast } from "@aurumiq/ui/components/sonner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { errorMessage, qs, rawApi } from "@/lib/api";
import type {
  AdminPlan,
  AdminPlanPutInput,
  AdminSubscription,
  BillingEvent,
  Paginated,
  PlanSyncResult,
} from "@/lib/types";

const BASE = "/api/v1/admin/billing";

export const billingKeys = {
  plans: ["admin", "billing", "plans"] as const,
  subscriptions: (status: string, page: number) =>
    ["admin", "billing", "subscriptions", { status, page }] as const,
  events: (page: number) => ["admin", "billing", "events", { page }] as const,
};

export function useAdminPlans() {
  return useQuery({
    queryKey: billingKeys.plans,
    queryFn: () => rawApi.get<{ items: AdminPlan[] }>(`${BASE}/plans`),
  });
}

export function useSubscriptions(status = "", page = 1) {
  return useQuery({
    queryKey: billingKeys.subscriptions(status, page),
    queryFn: () =>
      rawApi.get<Paginated<AdminSubscription>>(`${BASE}/subscriptions${qs({ status, page })}`),
  });
}

export function useBillingEvents(page = 1) {
  return useQuery({
    queryKey: billingKeys.events(page),
    queryFn: () => rawApi.get<Paginated<BillingEvent>>(`${BASE}/events${qs({ page })}`),
  });
}

export function usePutPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ code, ...input }: AdminPlanPutInput & { code: string }) =>
      rawApi.put<AdminPlan>(`${BASE}/plans/${code}`, input),
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: billingKeys.plans });
      toast.success(`Plan ${p.name} saved`);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
}

/** Errors are surfaced by the caller (503 STRIPE_NOT_CONFIGURED shows an inline notice). */
export function useSyncPlans() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reason?: string) => rawApi.post<PlanSyncResult>(`${BASE}/plans/sync`, { reason }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: billingKeys.plans });
      if (r.errors.length) toast.warning(`Synced ${r.synced.length}, ${r.errors.length} failed`);
      else toast.success(`Synced ${r.synced.length} plan(s) to Stripe`);
    },
  });
}
